import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { X, Wallet, ArrowRight, ShieldCheck } from "lucide-react";

const STORAGE_KEY = "energiekontract:exit-intent:dismissed";

/**
 * ExitIntentPopup — triggered when:
 *  - Desktop: mouse leaves viewport from the top (intent to close/switch tab)
 *  - Mobile: after 45 seconds on the page without scrolling further down
 *  - Triggered only ONCE per session (sessionStorage)
 *  - Only shown if `enabled` (typically: teaser mode + no payment yet)
 */
export default function ExitIntentPopup({ enabled = true, naheffingAmount = 0, savingsEstimate = 0 }) {
  const [open, setOpen] = useState(false);
  const triggeredRef = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    if (sessionStorage.getItem(STORAGE_KEY) === "1") return;

    const trigger = () => {
      if (triggeredRef.current) return;
      triggeredRef.current = true;
      setOpen(true);
    };

    // Desktop: mouse leaves from top of viewport
    const handleMouseLeave = (e) => {
      if (e.clientY <= 0 && (e.relatedTarget === null || e.relatedTarget === undefined)) {
        trigger();
      }
    };

    // Mobile fallback: trigger after 45s if user is still on page
    const mobileTimer = setTimeout(() => {
      // Only fire on touch devices (heuristic) to avoid double-firing desktop
      if (window.matchMedia && window.matchMedia("(hover: none)").matches) {
        trigger();
      }
    }, 45000);

    document.addEventListener("mouseleave", handleMouseLeave);
    return () => {
      document.removeEventListener("mouseleave", handleMouseLeave);
      clearTimeout(mobileTimer);
    };
  }, [enabled]);

  const close = () => {
    sessionStorage.setItem(STORAGE_KEY, "1");
    setOpen(false);
  };

  if (!open) return null;

  // Pick the strongest hook based on what the analysis found
  const hasNaheffing = naheffingAmount > 100;
  const hasSavings = savingsEstimate > 50;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-sm animate-in fade-in duration-200"
      role="dialog"
      aria-modal="true"
      onClick={close}
      data-testid="exit-intent-overlay"
    >
      <div
        className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="exit-intent-popup"
      >
        {/* Close */}
        <button
          onClick={close}
          className="absolute top-3 right-3 z-10 w-8 h-8 grid place-items-center rounded-full bg-white/90 hover:bg-white border border-slate-200 text-slate-500 hover:text-slate-900 transition-colors"
          aria-label="Sluiten"
          data-testid="exit-intent-close"
        >
          <X className="h-4 w-4" />
        </button>

        {/* Hero */}
        <div className="bg-gradient-to-br from-red-600 via-red-500 to-amber-500 px-6 pt-6 pb-7 text-white">
          <div className="inline-flex items-center gap-1.5 bg-white/20 backdrop-blur-sm rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] mb-3">
            <span>⚠</span>
            <span>Wacht — niet weggaan</span>
          </div>
          <h2 className="font-heading text-2xl font-bold leading-tight">
            {hasNaheffing
              ? `Je kunt een naheffing van €${Math.round(naheffingAmount)} voorkomen — gratis`
              : hasSavings
              ? `Bespaar tot €${Math.round(savingsEstimate)} per jaar — gratis`
              : "Voordat je gaat — bespaar honderden euro's"}
          </h2>
        </div>

        {/* Body */}
        <div className="px-6 py-6">
          <p className="text-sm text-slate-700 leading-relaxed mb-5">
            Je hoeft ons rapport <strong>niet eens te kopen</strong> om te besparen. We hebben 9 ACM-erkende leveranciers naast elkaar gezet en kunnen direct laten zien welke beste tarieven heeft voor jouw situatie.
          </p>

          {/* Benefits */}
          <ul className="space-y-2 mb-6">
            {[
              hasNaheffing ? `Voorkom je naheffing van €${Math.round(naheffingAmount)}` : "Zie direct welke leverancier €100+ goedkoper is",
              "9 ACM-erkende aanbieders naast elkaar",
              "Volledig gratis · geen account nodig",
            ].map((b, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                <span className="mt-1 h-1.5 w-1.5 rounded-full bg-emerald-600 flex-shrink-0" />
                <span>{b}</span>
              </li>
            ))}
          </ul>

          {/* CTAs */}
          <div className="space-y-2">
            <Link
              to="/vergelijk?from=exit"
              onClick={() => sessionStorage.setItem(STORAGE_KEY, "1")}
              className="w-full inline-flex items-center justify-center gap-2 bg-blue-700 hover:bg-blue-800 text-white font-semibold rounded-xl px-5 py-3.5 shadow-[0_8px_24px_-12px_rgba(29,78,216,0.5)] transition-all hover:-translate-y-0.5 text-sm"
              data-testid="exit-intent-cta-compare"
            >
              Vergelijk nu gratis
              <ArrowRight className="h-4 w-4" />
            </Link>
            <button
              onClick={close}
              className="w-full text-xs text-slate-500 hover:text-slate-700 py-2 transition-colors"
              data-testid="exit-intent-dismiss"
            >
              Nee dank je, ik wil eerst het rapport kopen
            </button>
          </div>

          {/* Trust */}
          <div className="mt-5 pt-4 border-t border-slate-100 flex items-center justify-center gap-3 text-[11px] text-slate-500">
            <span className="flex items-center gap-1">
              <ShieldCheck className="h-3 w-3 text-emerald-600" />
              ACM-erkend
            </span>
            <span>·</span>
            <span>Geen verplichtingen</span>
            <span>·</span>
            <span>Onafhankelijk advies</span>
          </div>
        </div>
      </div>
    </div>
  );
}
